import { replaceEqualDeep, useQuery } from '@tanstack/react-query'
import axios from 'axios'
import moment from 'moment'
import ExchangeService from 'src/services/ExchangeService'
import {
  normalizeExportOffers,
  normalizeFreeCarriersOffers,
  normalizeGeneralOffers,
  normalizeImportOffers,
  normalizePostImportOffers
} from 'src/features/stocks/ducks/normalizers'
import { getMapRouteDetails, getMapRouteCacheKey } from './mapsRoute'
import {
  ROUTE_CACHE_TIME,
  ROUTE_CACHE_LIMIT,
  isCacheableRoute,
  readRouteCache,
  writeRouteCache
} from './routeCacheStorage'
import { scheduleMapQueryRefresh } from './scheduleMapQueryRefresh'
import { getSharedRequest, throwIfAborted } from './sharedRequest'
import {
  EXCHANGE_MAP_TYPES,
  getMapDistanceKm,
  getRoundTripCenterMapLocation,
  getRoundTripCandidateTypes,
  isPreplannedRoundTripOffer,
  isSameOfferLocation,
  normalizeMapLayers
} from './offerMapUtils'

export const MAP_LIST_PAGE_LIMIT = 100

const MAP_LIST_PAGE_REQUEST_CONCURRENCY = 4
const MAP_POINTS_STALE_TIME = 15 * 1000
const MAP_LIST_STALE_TIME = 15 * 1000
const ROUND_TRIP_STALE_TIME = 60 * 1000
const MAP_CACHE_TIME = 5 * 60 * 1000
const UNAVAILABLE_ROUTE_CACHE_TIME = 60 * 1000
const routeMemoryCache = new Map()
const unavailableRoutes = new Map()
const routeRequests = new Map()
const routeQueryRequests = new WeakMap()

const MAP_EXCHANGES = {
  [EXCHANGE_MAP_TYPES.EXPORT]: 'export-cargo',
  [EXCHANGE_MAP_TYPES.IMPORT]: 'import-cargo',
  [EXCHANGE_MAP_TYPES.GENERAL]: 'general-cargo',
  [EXCHANGE_MAP_TYPES.POST_IMPORT]: 'post-import-containers',
  [EXCHANGE_MAP_TYPES.FREE_CARRIERS]: 'free-carriers'
}

const MAP_NORMALIZERS = {
  [EXCHANGE_MAP_TYPES.EXPORT]: normalizeExportOffers,
  [EXCHANGE_MAP_TYPES.IMPORT]: normalizeImportOffers,
  [EXCHANGE_MAP_TYPES.GENERAL]: normalizeGeneralOffers,
  [EXCHANGE_MAP_TYPES.POST_IMPORT]: normalizePostImportOffers,
  [EXCHANGE_MAP_TYPES.FREE_CARRIERS]: normalizeFreeCarriersOffers
}

const ROUND_TRIP_FILTER_CONFIG = {
  [EXCHANGE_MAP_TYPES.EXPORT]: {
    originDateFrom: 'loading_date_from',
    originDateTo: 'loading_date_to'
  },
  [EXCHANGE_MAP_TYPES.GENERAL]: {
    originDateFrom: 'loading_date_from',
    originDateTo: 'loading_date_to'
  }
}

const withRawOffer = offer => ({ ...offer, raw: offer })

const getFormattedMapFilters = filters =>
  Object.keys(filters).reduce(
    (formattedFilters, key) => ({
      ...formattedFilters,
      [`filter[${key}]`]: filters[key]
    }),
    {}
  )

const getMapGroupKey = group =>
  group
    ? `${group.role}:${(group.locationIds || [])
        .map(String)
        .sort()
        .join(',')}`
    : null

const ROUTE_PROFILE_FALLBACKS = {
  summary: ['summary', 'primary', 'alternatives'],
  primary: ['primary', 'summary', 'alternatives'],
  alternatives: ['alternatives']
}

const projectRoute = (route, profile, sourceProfile) => {
  if (profile === 'summary') return { distanceKm: route.distanceKm }
  if (profile === sourceProfile) return route
  return {
    ...route,
    alternates: []
  }
}

const rememberRoute = (key, entry) => {
  routeMemoryCache.delete(key)
  routeMemoryCache.set(key, entry)
  if (routeMemoryCache.size > ROUTE_CACHE_LIMIT) {
    routeMemoryCache.delete(routeMemoryCache.keys().next().value)
  }
}

const readCompatibleRoute = async (
  from,
  to,
  profiles,
  signal,
  requestedProfile
) => {
  const [profile, ...remainingProfiles] = profiles
  if (!profile) return null
  const key = getMapRouteCacheKey(from, to, profile)
  const entry = await readRouteCache(key, profile)
  throwIfAborted(signal)
  if (
    entry &&
    entry.expiresAt > Date.now() &&
    isCacheableRoute(entry.route, requestedProfile)
  ) {
    return { key, entry, profile }
  }
  return readCompatibleRoute(
    from,
    to,
    remainingProfiles,
    signal,
    requestedProfile
  )
}

const getLocallyCachedRoute = async (from, to, signal, profile) => {
  throwIfAborted(signal)
  const profiles = ROUTE_PROFILE_FALLBACKS[profile]
  const cached = profiles
    .map(candidate => {
      const key = getMapRouteCacheKey(from, to, candidate)
      const entry = routeMemoryCache.get(key)
      if (!entry) return null
      if (entry.expiresAt <= Date.now()) {
        routeMemoryCache.delete(key)
        return null
      }
      return { key, entry, profile: candidate }
    })
    .find(
      candidate => candidate && isCacheableRoute(candidate.entry.route, profile)
    )
  if (cached) {
    rememberRoute(cached.key, cached.entry)
    return projectRoute(cached.entry.route, profile, cached.profile)
  }

  const unavailableKey = getMapRouteCacheKey(from, to, profile)
  const unavailable = unavailableRoutes.get(unavailableKey)
  if (unavailable) {
    if (unavailable.expiresAt > Date.now()) throw unavailable.error
    unavailableRoutes.delete(unavailableKey)
  }

  // All backend profiles carry geometry; only alternatives need a richer request.
  const requestProfile =
    profiles.find(candidate =>
      routeRequests.has(getMapRouteCacheKey(from, to, candidate))
    ) || profile
  const cacheKey = getMapRouteCacheKey(from, to, requestProfile)

  // Query clients may share one transport request, but cancel independently.
  return getSharedRequest(
    routeRequests,
    cacheKey,
    async requestSignal => {
      const stored = await readCompatibleRoute(
        from,
        to,
        ROUTE_PROFILE_FALLBACKS[requestProfile],
        requestSignal,
        requestProfile
      )
      throwIfAborted(requestSignal)
      if (stored) {
        rememberRoute(stored.key, stored.entry)
        return requestProfile === 'summary'
          ? stored.entry.route
          : projectRoute(stored.entry.route, requestProfile, stored.profile)
      }
      let route
      try {
        route = await getMapRouteDetails(
          from,
          to,
          requestSignal,
          requestProfile
        )
      } catch (error) {
        throwIfAborted(requestSignal)
        if (
          error.status === 422 &&
          [42200, 42210, 42211, 42213, 42214, 42215].includes(error.code)
        ) {
          // A new render or another query client cannot make the same route
          // possible. Keep this result briefly, without persisting a failure.
          unavailableRoutes.delete(cacheKey)
          unavailableRoutes.set(cacheKey, {
            error,
            expiresAt: Date.now() + UNAVAILABLE_ROUTE_CACHE_TIME
          })
          if (unavailableRoutes.size > ROUTE_CACHE_LIMIT) {
            unavailableRoutes.delete(unavailableRoutes.keys().next().value)
          }
        }
        throw error
      }
      throwIfAborted(requestSignal)
      const entry = { route, expiresAt: Date.now() + ROUTE_CACHE_TIME }
      if (isCacheableRoute(route, requestProfile)) {
        await writeRouteCache(cacheKey, route, entry.expiresAt, requestProfile)
        throwIfAborted(requestSignal)
        rememberRoute(cacheKey, entry)
      }
      return route
    },
    signal
  ).then(route => projectRoute(route, profile, requestProfile))
}

const requestWithSignal = async (signal, request) => {
  if (!signal) return request()

  const source = axios.CancelToken.source()
  const cancelRequest = () => source.cancel('Query was cancelled')
  if (signal.aborted) {
    cancelRequest()
    source.token.throwIfRequested()
  }
  signal.addEventListener('abort', cancelRequest, { once: true })

  try {
    return await request({ cancelToken: source.token })
  } finally {
    signal.removeEventListener('abort', cancelRequest)
  }
}

export const exchangeOffersMapKeys = {
  all: ['exchange-offers-map'],
  tab: (exchangeType, activeTab) => [
    ...exchangeOffersMapKeys.all,
    exchangeType,
    'tab',
    String(activeTab || '')
  ],
  pointsScope: (exchangeType, activeTab) => [
    ...exchangeOffersMapKeys.tab(exchangeType, activeTab),
    'points'
  ],
  points: (exchangeType, activeTab, filters) => [
    ...exchangeOffersMapKeys.pointsScope(exchangeType, activeTab),
    filters
  ],
  listsScope: (exchangeType, activeTab) => [
    ...exchangeOffersMapKeys.tab(exchangeType, activeTab),
    'lists'
  ],
  listSearch: (exchangeType, activeTab, group, filters) => [
    ...exchangeOffersMapKeys.listsScope(exchangeType, activeTab),
    getMapGroupKey(group),
    filters
  ],
  list: (exchangeType, activeTab, group, filters, page) => [
    ...exchangeOffersMapKeys.listSearch(
      exchangeType,
      activeTab,
      group,
      filters
    ),
    page
  ],
  roundTrip: (exchangeType, selectedMapOffer, filters) => [
    ...exchangeOffersMapKeys.all,
    'round-trip',
    exchangeType,
    selectedMapOffer ? selectedMapOffer.id : null,
    selectedMapOffer && selectedMapOffer.from && selectedMapOffer.from.location
      ? [
          selectedMapOffer.from.location.id || null,
          selectedMapOffer.from.location.countryCode || null,
          selectedMapOffer.from.location.name || null
        ]
      : null,
    selectedMapOffer && selectedMapOffer.to
      ? selectedMapOffer.to.rawDate || selectedMapOffer.to.date
      : null,
    selectedMapOffer && selectedMapOffer.to && selectedMapOffer.to.mapLocation
      ? selectedMapOffer.to.mapLocation.coordinates
      : null,
    {
      exchangeTypes: filters.exchangeTypes,
      daysAhead: filters.daysAhead,
      maxDistanceKm: filters.maxDistanceKm,
      createdAtFrom: filters.createdAtFrom || null,
      createdAtTo: filters.createdAtTo || null
    }
  ],
  route: (from, to, profile = 'primary') => [
    ...exchangeOffersMapKeys.all,
    'route',
    getMapRouteCacheKey(from, to, profile)
  ]
}

const getAllMapListPages = async ({ getPage, normalize }) => {
  const firstPage = await getPage(1)
  const pagination = (firstPage.response && firstPage.response.meta) || {}
  const reportedLastPage = Number(pagination.lastPage)
  const lastPage =
    Number.isSafeInteger(reportedLastPage) && reportedLastPage > 0
      ? reportedLastPage
      : 1
  const offersById = new Map()
  const appendOffers = result => {
    const offers = result.data || []
    offers.forEach(offer => offersById.set(offer.id, offer))
  }
  const pages = [firstPage]

  let nextPage = 2
  let stopped = false
  const loadNextPage = async () => {
    if (stopped || nextPage > lastPage) return

    const page = nextPage
    nextPage += 1
    try {
      pages[page - 1] = await getPage(page)
    } catch (error) {
      stopped = true
      throw error
    }
    return loadNextPage()
  }
  const workerCount = Math.min(
    MAP_LIST_PAGE_REQUEST_CONCURRENCY,
    Math.max(lastPage - 1, 0)
  )
  await Promise.all(Array.from({ length: workerCount }, loadNextPage))
  // Keep server order (including duplicate precedence) despite response timing.
  pages.forEach(appendOffers)

  return normalize(Array.from(offersById.values()).map(withRawOffer))
}

export const getRoundTripCandidateOffers = async (
  exchangeType,
  selectedMapOffer,
  roundTripFilters,
  signal
) => {
  if (isPreplannedRoundTripOffer(selectedMapOffer)) {
    return {
      offers: [],
      mapLayers: [],
      successfulExchangeTypes: [],
      failedExchangeTypes: []
    }
  }
  const selectedDestinationDateValue = selectedMapOffer.to.rawDate
  const selectedDestinationMoment = moment(selectedDestinationDateValue)
  const selectedDestinationDate = selectedDestinationMoment.isValid()
    ? selectedDestinationMoment.format('YYYY-MM-DD')
    : selectedDestinationDateValue
  if (!selectedDestinationDateValue) {
    return { offers: [], mapLayers: [] }
  }

  const candidateRequests = getRoundTripCandidateTypes(exchangeType)
    .filter(candidateExchangeType =>
      roundTripFilters.exchangeTypes.includes(candidateExchangeType)
    )
    .filter(
      candidateExchangeType =>
        candidateExchangeType !== EXCHANGE_MAP_TYPES.GENERAL ||
        process.env.GATSBY_HIDE_GENERAL_CARGO !== 'true'
    )
    .map(candidateExchangeType => ({
      exchangeType: candidateExchangeType,
      exchange: MAP_EXCHANGES[candidateExchangeType],
      normalize: MAP_NORMALIZERS[candidateExchangeType],
      filterConfig: ROUND_TRIP_FILTER_CONFIG[candidateExchangeType]
    }))
  const results = await Promise.all(
    candidateRequests.map(async candidateRequest => {
      try {
        const { filterConfig } = candidateRequest
        const filter = {
          ...(roundTripFilters.createdAtFrom
            ? { created_at_from: roundTripFilters.createdAtFrom }
            : {}),
          ...(roundTripFilters.createdAtTo
            ? { created_at_to: roundTripFilters.createdAtTo }
            : {}),
          [filterConfig.originDateFrom]: selectedDestinationDate,
          [filterConfig.originDateTo]: moment(selectedDestinationDate)
            .add(roundTripFilters.daysAhead, 'day')
            .format('YYYY-MM-DD')
        }
        // Destination matching also accepts name aliases with different IDs.
        // Apply it locally instead of narrowing the API request to one ID.
        const mapResult = await requestWithSignal(signal, config =>
          ExchangeService.getOffersMap(
            candidateRequest.exchange,
            getFormattedMapFilters(filter),
            config
          )
        )
        const mapLayers = [
          {
            exchangeType: candidateRequest.exchangeType,
            layers: mapResult.data.layers || []
          }
        ]
        const allOriginGroups = normalizeMapLayers(
          mapResult.data.layers
        ).filter(group => group.role === 'origin')
        const roundTripCenterMapLocation = getRoundTripCenterMapLocation(
          selectedMapOffer,
          allOriginGroups.map(group => group.mapLocation)
        )
        const originGroups = allOriginGroups.filter(group => {
          if (!roundTripCenterMapLocation) {
            return isSameOfferLocation(
              selectedMapOffer.to.location,
              group.mapLocation
            )
          }

          const distanceKm = getMapDistanceKm(
            roundTripCenterMapLocation,
            group.mapLocation
          )
          return (
            distanceKm !== null &&
            distanceKm <= Number(roundTripFilters.maxDistanceKm)
          )
        })
        const locationIds = Array.from(
          new Set(
            originGroups.reduce(
              (ids, group) => ids.concat(group.locationIds),
              []
            )
          )
        )
        if (locationIds.length === 0) {
          return { error: false, offers: [], mapLayers }
        }

        try {
          const offers = await getAllMapListPages({
            getPage: page =>
              requestWithSignal(signal, config =>
                ExchangeService.getOffersMapList(
                  candidateRequest.exchange,
                  {
                    layer: 'origin',
                    locationIds,
                    filter,
                    page,
                    limit: MAP_LIST_PAGE_LIMIT
                  },
                  config
                )
              ),
            normalize: candidateRequest.normalize
          })
          return {
            error: false,
            offers: offers.map(offer => ({
              exchangeType: candidateRequest.exchangeType,
              offer,
              mapLayers: mapResult.data.layers
            })),
            mapLayers
          }
        } catch (error) {
          return { error: true, offers: [], mapLayers }
        }
      } catch (error) {
        return { error: true, offers: [], mapLayers: [] }
      }
    })
  )

  if (signal && signal.aborted) {
    throw new Error('Return offer search was cancelled')
  }

  const successfulResults = results.filter(result => !result.error)
  if (results.length > 0 && successfulResults.length === 0) {
    throw new Error('Failed to load return offers')
  }

  return {
    successfulExchangeTypes: candidateRequests
      .filter((request, index) => !results[index].error)
      .map(request => request.exchangeType),
    failedExchangeTypes: candidateRequests
      .filter((request, index) => results[index].error)
      .map(request => request.exchangeType),
    offers: successfulResults.reduce(
      (combinedOffers, result) => combinedOffers.concat(result.offers),
      []
    ),
    mapLayers: successfulResults.reduce(
      (combinedLayers, result) => combinedLayers.concat(result.mapLayers),
      []
    )
  }
}

// React Query supplies previous data only from this exact search key. Keep
// failed exchanges until they recover; a successful empty result still removes
// their old offers. Preserve structural sharing for unchanged rows and layers.
const shareRoundTripResults = (previous, next) => {
  const failedTypes = new Set(next.failedExchangeTypes || [])
  if (!previous || failedTypes.size === 0) {
    return replaceEqualDeep(previous, next)
  }

  const keepFailed = items =>
    items.filter(item => failedTypes.has(item.exchangeType))
  return replaceEqualDeep(previous, {
    ...next,
    offers: next.offers.concat(keepFailed(previous.offers)),
    mapLayers: next.mapLayers.concat(keepFailed(previous.mapLayers))
  })
}

export const useExchangeMapPointsQuery = ({
  exchangeType,
  activeTab,
  filters,
  enabled
}) => {
  const exchange = MAP_EXCHANGES[exchangeType]

  return useQuery({
    queryKey: exchangeOffersMapKeys.points(exchangeType, activeTab, filters),
    queryFn: async ({ signal }) => {
      const result = await requestWithSignal(signal, config =>
        ExchangeService.getOffersMapByTab(exchange, activeTab, filters, config)
      )

      return {
        layers: result.data.layers || [],
        offersCount:
          Number(
            result.response &&
              result.response.meta &&
              result.response.meta.offersCount
          ) || 0
      }
    },
    enabled: Boolean(enabled && exchange && activeTab),
    staleTime: MAP_POINTS_STALE_TIME,
    cacheTime: MAP_CACHE_TIME,
    retry: 1,
    refetchOnWindowFocus: false
  })
}

export const useExchangeMapOffersQuery = ({
  exchangeType,
  activeTab,
  group,
  filters,
  page,
  enabled
}) => {
  const exchange = MAP_EXCHANGES[exchangeType]
  const normalize = MAP_NORMALIZERS[exchangeType]
  const groupKey = getMapGroupKey(group)
  const searchKey = JSON.stringify(
    exchangeOffersMapKeys.listSearch(exchangeType, activeTab, group, filters)
  )

  return useQuery({
    queryKey: exchangeOffersMapKeys.list(
      exchangeType,
      activeTab,
      group,
      filters,
      page
    ),
    queryFn: async ({ signal }) => {
      const result = await requestWithSignal(signal, config =>
        ExchangeService.getOffersMapList(
          exchange,
          {
            layer: group.role,
            locationIds: group.locationIds,
            filter: filters,
            page,
            limit: MAP_LIST_PAGE_LIMIT
          },
          config
        )
      )

      return {
        groupKey,
        searchKey,
        offers: normalize((result.data || []).map(withRawOffer)),
        pagination: (result.response && result.response.meta) || {}
      }
    },
    enabled: Boolean(enabled && exchange && normalize && group),
    keepPreviousData: true,
    staleTime: MAP_LIST_STALE_TIME,
    cacheTime: MAP_CACHE_TIME,
    retry: 1,
    refetchOnWindowFocus: false
  })
}

export const useExchangeRoundTripQuery = ({
  exchangeType,
  selectedMapOffer,
  filters,
  enabled,
  liveRefresh = false
}) =>
  useQuery({
    queryKey: exchangeOffersMapKeys.roundTrip(
      exchangeType,
      selectedMapOffer,
      filters
    ),
    queryFn: ({ signal }) =>
      getRoundTripCandidateOffers(
        exchangeType,
        selectedMapOffer,
        filters,
        signal
      ),
    enabled: Boolean(enabled && selectedMapOffer),
    structuralSharing: shareRoundTripResults,
    staleTime: ROUND_TRIP_STALE_TIME,
    cacheTime: MAP_CACHE_TIME,
    retry: false,
    retryOnMount: false,
    refetchOnMount: true,
    refetchOnWindowFocus: liveRefresh ? 'always' : true,
    refetchOnReconnect: liveRefresh ? 'always' : false
  })

export const invalidateExchangeMapQueries = (
  queryClient,
  exchangeType,
  activeTab
) => {
  scheduleMapQueryRefresh(queryClient, {
    queryKey: exchangeOffersMapKeys.pointsScope(exchangeType, activeTab)
  })
  scheduleMapQueryRefresh(queryClient, {
    queryKey: exchangeOffersMapKeys.listsScope(exchangeType, activeTab)
  })
}

export const fetchExchangeOfferRoute = (
  queryClient,
  from,
  to,
  signal,
  profile = 'primary'
) => {
  let requests = routeQueryRequests.get(queryClient)
  if (!requests) {
    requests = new Map()
    routeQueryRequests.set(queryClient, requests)
  }
  const queryKey = exchangeOffersMapKeys.route(from, to, profile)

  // Track every effect before fetchQuery deduplicates identical query keys.
  return getSharedRequest(
    requests,
    getMapRouteCacheKey(from, to, profile),
    async requestSignal => {
      const cancelQuery = () =>
        queryClient.cancelQueries({ queryKey, exact: true })
      requestSignal.addEventListener('abort', cancelQuery, { once: true })
      try {
        return await queryClient.fetchQuery({
          queryKey,
          queryFn: ({ signal: querySignal }) =>
            getLocallyCachedRoute(from, to, querySignal, profile),
          // The stored expiry is fixed. React Query's fetch timestamp must not
          // extend it when an existing route is restored after a page reload.
          staleTime: 0,
          // The bounded local cache and IndexedDB own completed routes.
          // Release React Query's extra copy as soon as it is inactive.
          cacheTime: 0,
          retry: false
        })
      } finally {
        requestSignal.removeEventListener('abort', cancelQuery)
      }
    },
    signal
  )
}

export { getMapGroupKey }
